import { useEffect, useMemo, useRef, useState, useCallback, Fragment } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Header } from "./header";
import "./style.css";
import { Separator } from "@/components/ui/separator";

import dashboardsvg from "@/assets/dashboard-svg.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { ResourceTypes } from "@/types/roles";
import { useDispatch } from "react-redux";
import {
  backPageCount,
  resetBackPageCount,
  setCode,
  setCounter,
} from "@/redux/counterSlice";
import { extractLinks } from "@/lib/helper";
import {
  useGetCountersQuery,
  useUpdateCountersMutation,
} from "@/services/counterApi";
import { toast } from "../use-toast";
import { LoadingButton } from "./loading-button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../mobile-tooltip";
import { resetInvoice } from "@/redux/invoiceSlice";
import { ChevronLeft } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { setSidePanelDispatch } from "@/redux/sidePanelSlice";
import { resetWalletInvoice } from "@/redux/walletRefundSlice";
import { useGetOrganizationQuery } from "@/services/organizationApi";
import { ConfirmationDialog } from "./confirmation-dailog";
const { VITE_VIEW_S3_URL } = import.meta.env;

const DashboardLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const pos_count = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).pos_count ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const { data: assignedCounter } = useGetCountersQuery({
    query: `status=active${pos_count !== "full_access" ? `&staff_id=${userInfo?.user?.id}` : ""}`,
  });

  const assignedCounterData = useMemo(() => {
    return Array.isArray(assignedCounter?.data) ? assignedCounter?.data : [];
  }, [assignedCounter]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { open } = useSelector((state: RootState) => state.sidePanel);
  const [seperatePanel, setSeperatePanel] = useState<ResourceTypes[]>([]);
  const [backtogym, setBacktogym] = useState<string>("");
  const [sidePanel, setSidePanel] = useState<ResourceTypes[]>([]);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [showNameTooltip, setShowNameTooltip] = useState<boolean>(false);
  const nameRef = useRef<HTMLSpanElement>(null);

  const { code, counter_number } = useSelector(
    (state: RootState) => state.counter
  );
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;


  const { data: organizationData, refetch } = useGetOrganizationQuery(orgId as number, {
    skip: !orgId
  })

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(localStorage.getItem("sidePanelState") == "true" || open);
  const [assignCounter, result] = useUpdateCountersMutation();
  const manualNavigationRef = useRef(false); // to control navigation in routes

  // Check if organization name is truncated
  const checkTextTruncation = useCallback(() => {
    if (nameRef.current) {
      const isOverflowing = nameRef.current.scrollWidth > nameRef.current.clientWidth;
      setShowNameTooltip(isOverflowing);
    }
  }, []);

  // Check truncation when organization data changes or sidebar state changes
  useEffect(() => {
    if (organizationData?.name) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(checkTextTruncation, 0);
    }
  }, [organizationData?.name, isSidebarOpen, checkTextTruncation]);

  const handleCancel = () => {
    setIsOpenConfirmation(false);
  }

  const handleConfirm = async () => {
    setIsOpenConfirmation(false);
    dispatch(setCode(null));
    dispatch(setCounter(null));
    dispatch(resetBackPageCount());
    dispatch(resetInvoice());
    dispatch(resetWalletInvoice());
    const payload = {
      id:
        counter_number ??
        Number(localStorage.getItem("counter_number") as string),
      is_open: false,
    };
    const resp = await assignCounter(payload).unwrap();
    navigate("/", { replace: true });
  }

  // Use useEffect to fetch and set sidePanel from localStorage
  useEffect(() => {
    const sidepanel = localStorage.getItem("sidepanel");
    if (sidepanel) {
      try {
        const decodedSidepanel = JSON.parse(atob(sidepanel));
        const links = extractLinks(decodedSidepanel);
        setSidePanel(decodedSidepanel);
        setBacktogym(links[0]);
      } catch (error) {
        console.error("Error decoding Base64 string sidepanel:", error);
      }
    }
  }, []);

  useEffect(() => {
    const posPanel = localStorage.getItem("posPanel");
    if (code == "pos" && posPanel) {
      try {
        const decodedPosPanel = JSON.parse(atob(posPanel));
        setSeperatePanel(decodedPosPanel.children);
      } catch (error) {
        console.error("Error decoding Base64 string posPanel:", error);
      }
    }
  }, [code]);


  useEffect(() => {
    if (!manualNavigationRef.current) {
      if (pathname.includes("pos") && counter_number == null && code == null) {
        dispatch(setCode("pos"));
        navigate("/counter-selection", { replace: true });
      } else if (!pathname.includes("pos") && pathname !== "/counter-selection") {
        // to reset the sidepanel
        localStorage.removeItem("code");
        dispatch(setCode(null));
        dispatch(setCounter(null));
        dispatch(resetBackPageCount());
        dispatch(resetInvoice());
        dispatch(resetWalletInvoice());
      }
    } else {
      // Reset the ref after intentional navigation
      manualNavigationRef.current = false;
    }
  }, [pathname, counter_number]);

  const closeCounter = async () => {
    try {
      const payload = {
        id:
          counter_number ??
          Number(localStorage.getItem("counter_number") as string),
        is_open: false,
      };

      const resp = await assignCounter(payload).unwrap();
      if (resp) {
        dispatch(setCode(null));
        dispatch(setCounter(null));
        dispatch(resetBackPageCount());
        dispatch(resetInvoice());
        dispatch(resetWalletInvoice());
        toast({
          variant: "success",
          title: "Counter Closed Successfully",
        });

        localStorage.removeItem("code");

        manualNavigationRef.current = true; // Mark this as a manual navigation
        if (assignedCounterData.length > 1) {
          navigate("/counter-selection");
        } else {
          navigate(backtogym, { replace: true });
        }
      }
    } catch (error) {
      console.error("Error assigning counter:", error);
    }
  };

  const isActiveLink = (targetPath: string) => {
    // Exact match
    if (pathname == targetPath) {
      return true;
    }

    // Match `/admin/members` with sub-paths like `/admin/members/detail/48
    if (pathname.startsWith(targetPath) && !targetPath.includes("facility")) {
      const remainingPath = pathname.slice(targetPath.length);
      // Ensure the remaining path starts with "/" or is empty
      return remainingPath === "" || remainingPath.startsWith("/");
    }

    // Default case: No match
    return false;
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
    dispatch(setSidePanelDispatch(!isSidebarOpen))
  };

  return (
    <div className="font-poppins flex h-full overflow-hidden w-full relative ">
      <nav
        className={`bg-white border-r text-black shadow-md transition-all duration-100 ${isSidebarOpen ? "w-[275px]" : "w-[72px]"} h-screen  custom-scrollbar-right `}
      >
        <div
          style={{ direction: "ltr" }}
          className="flex h-16 items-center justify-between px-4 border-gradient sticky top-0 z-30 bg-white "
        >
          {code !== "pos" && (
            <Link
              to="/"
              className="flex items-center gap-1 font-semibold "
              onClick={() => {

                dispatch(setCode(null));
              }}
            >
              <img
                src={organizationData?.logo ? `${VITE_VIEW_S3_URL}/${organizationData?.logo}` : dashboardsvg}
                className={`size-8 rounded-[50%]`}
                alt="logo"
              />
              {showNameTooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        ref={nameRef}
                        className={`${!isSidebarOpen && "hidden"} text-2xl font-extrabold whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer `}
                        style={{ fontFamily: "Jockey One", maxWidth: "200px" }}
                      >
                        {organizationData?.name}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      className="bg-white text-sm text-primary border-none max-w-xs z-[1000]"
                      side="bottom"
                    >
                      <p>{organizationData?.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span
                  ref={nameRef}
                  className={`${!isSidebarOpen && "hidden"} text-2xl font-extrabold whitespace-nowrap overflow-hidden text-ellipsis`}
                  style={{ fontFamily: "Jockey One", maxWidth: "200px" }}
                >
                  {organizationData?.name}
                </span>
              )}
            </Link>
          )}

          {code == "pos" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-2 font-semibold cursor-pointer"
                    onClick={() => setIsOpenConfirmation(true)}
                  >
                    <i className="rounded-[50%] fa fa-arrow-left px-2 py-0.5 text-lg border-2 border-primary text-primary"></i>
                    <span
                      className={`${!isSidebarOpen && "hidden"} text-2xl text-center font-extrabold whitespace-nowrap overflow-hidden text-ellipsis`}
                      style={{ fontFamily: "Jockey One", maxWidth: "200px" }}
                    >
                      Back to Gym
                    </span>
                  </div>
                </TooltipTrigger>
                {!isSidebarOpen && (
                  <TooltipContent
                    className="bg-white text-sm text-primary border-none ml-2"
                    side="right"
                  >
                    <p>Back to Gym</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Uncomment the button if needed */}
          <button
            onClick={toggleSidebar}
            className={`absolute  top-[3.2rem] -right-1.5  z-50 size-6 flex items-center rounded-md bg-white transition  border-[1px] border-gradient p-0 `}
          >
            {isSidebarOpen ? (
              <ChevronLeft className=" text-gray-700 font-bold pr-1" />
            ) : (
              <ChevronRight className=" text-gray-700 font-bold pr-1" />
            )}
          </button>
        </div>
        <TooltipProvider>
          <div
            style={{ direction: "ltr" }}
            className="flex flex-col gap-2 px-2 py-2 "
          >
            {code !== "pos" &&
              sidePanel &&
              sidePanel?.map((item: any, i: number) => (
                <Fragment key={item.link || i}>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      {item.children && item.children?.length == 0 && (
                        <Link
                          key={i}
                          to={item.link}
                          onClick={(e) => {
                            if (item.code == "daily_check_ins") {
                              e.preventDefault();
                              window.open(item.link, '_blank');
                              return;
                            }
                            dispatch(setCode(item.code == "pos" ? item.code : null))
                          }}
                          className={`flex items-center gap-2 rounded-md p-1 transition-colors  ${isSidebarOpen ? "justify-start text-sm" : "justify-center text-lg"} ${isActiveLink(item.link) ? "bg-primary hover:bg-primary" : "hover:bg-hoverprimary  "}`}
                        >
                          <div
                            className={` w-7 h-7 ${isActiveLink(item.link) ? "bg-[#3ED13E]" : "bg-gray-100"} rounded-lg justify-center flex items-center`}
                          >
                            <i
                              className={`${item.icon} text-md ${isActiveLink(item.link) ? "" : "text-gray-500 stroke-current"}`}
                            ></i>
                          </div>
                          <span className={`${!isSidebarOpen && "hidden"}`}>
                            {item.name}
                          </span>
                        </Link>
                      )}
                    </TooltipTrigger>
                    {!isSidebarOpen && (
                      <TooltipContent
                        className="bg-white text-sm text-primary border-none"
                        side="right"
                      >
                        <p>{item.name}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>

                  {item.children &&
                    item.children?.length > 0 &&
                    (isSidebarOpen ? (
                      <Accordion type="single" collapsible >
                        <AccordionItem value="item-1 !border-none" id="accordion" onClick={(e) => {


                          dispatch(
                            setCode(item.code == "pos" ? item.code : null)
                          );
                          item.code == "pos" && navigate(item.link);
                        }}>
                          <AccordionTrigger
                            className={`flex items-center gap-2 rounded-md p-1 transition-colors !no-underline ${isSidebarOpen ? "justify-between text-sm" : "justify-center text-lg "} `}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-7 h-7 bg-gray-100 rounded-lg justify-center flex items-center`}
                              >
                                <i
                                  className={`${item.icon} w-4 h-4 text-gray-500 stroke-current`}
                                ></i>
                              </div>
                              <span
                                className={`${!isSidebarOpen && "hidden"} font-normal`}
                              >
                                {item.name}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="ml-7 flex flex-col  gap-1 mt-2 [data-state=closed]:animate-accordion-up [data-state=open]:animate-accordion-down ">
                              {item.children?.map((child: any, index: number) => (
                                <Link
                                  key={Number(index)}
                                  to={child.link}
                                  onClick={(e) => {
                                    if (child.code == "daily_check_ins") {
                                      e.preventDefault();
                                      window.open(child.link, '_blank');
                                      return;
                                    }
                                    dispatch(
                                      setCode(
                                        item.code == "pos" ? item.code : null
                                      )
                                    )
                                  }
                                  }
                                  className={`justify-start rounded-md px-3  py-1 ${isActiveLink(child.link) ? "bg-primary text-secondary" : "hover:bg-muted"} ${!isSidebarOpen && "hidden"}`}
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <SidebarPopover
                        item={item}
                        isActiveLink={isActiveLink}
                      />
                    ))}
                </Fragment>
              ))}

            {/* pos panel */}
            {code == "pos" && seperatePanel && (
              <>
                {seperatePanel.map((item, i) => (
                  <Tooltip key={i}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.link}
                        onClick={() =>{
                           dispatch(backPageCount(1))
                           dispatch(resetInvoice())
                           dispatch(resetWalletInvoice())
                        }}
                        className={`flex items-center gap-2 rounded-md p-1 transition-colors  ${isSidebarOpen ? "justify-start text-sm" : "justify-center text-lg"} ${isActiveLink(item.link) ? "bg-primary hover:bg-primary" : "hover:bg-hoverprimary  "}`}
                      >
                        <div
                          className={` w-7 h-7 ${isActiveLink(item.link) ? "bg-[#3ED13E]" : "bg-gray-100"} rounded-lg justify-center flex items-center`}
                        >
                          <i
                            className={`${item.icon} text-md ${isActiveLink(item.link) ? "" : "text-gray-500 stroke-current"}`}
                          ></i>
                        </div>
                        <span className={`${!isSidebarOpen && "hidden"}`}>
                          {item.name}
                        </span>
                      </Link>
                    </TooltipTrigger>
                    {!isSidebarOpen && (
                      <TooltipContent
                        className="bg-white text-sm text-primary border-none"
                        side="right"
                      >
                        <p>{item.name}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
                <div className="pt-4 w-full flex  flex-col gap-2">
                  <div className="pb-4">
                    <Separator />
                  </div>
                  <div className="flex justify-center w-full">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`flex justify-center ${isSidebarOpen ? "w-40" : "w-10"}`}>
                          <LoadingButton
                            className={`flex justify-center items-center w-full gap-2 rounded-md p-1 text-gray-900 transition-colors hover:bg-primary`}
                            loading={result.isLoading}
                            disabled={result.isLoading}
                            onClick={closeCounter}
                          >
                            {!result.isLoading && (
                              <i className="fa-solid fa-arrow-up-from-bracket -rotate-90"></i>
                            )}
                            <span className={`text-sm ${!isSidebarOpen && "hidden"}`}>
                              Close Counter
                            </span>
                          </LoadingButton>
                        </div>
                      </TooltipTrigger>
                      {!isSidebarOpen && (
                        <TooltipContent
                          className="bg-white text-sm text-primary border-none"
                          side="right"
                        >
                          <p>Close Counter</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>
              </>
            )}
          </div >
        </TooltipProvider>

      </nav>
      <main className=" flex-1 h-screen bg-outletcolor  custom-scrollbar w-[calc(100%-275px)]">
          <Header />
          <div className="w-full mx-auto max-w-[2000px] ">
            <Outlet />
          </div>
      </main>
      <ConfirmationDialog
        isOpen={isOpenConfirmation}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title="Are you sure you want to leave the POS?"
        message={``}
      />
    </div >
  );
};

export default DashboardLayout;


interface SidebarPopoverProps {
  item: {
    icon: string;
    link: string;
    name: string;
    children: ResourceTypes[];
  };
  isActiveLink: (path: string) => boolean;
}

export const SidebarPopover = ({ item, isActiveLink }: SidebarPopoverProps) => {
  const navigate = useNavigate();

  return (
    <Popover>
      <PopoverTrigger className="group flex items-center gap-2 rounded-md p-1 transition-colors justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary"
                  } rounded-lg flex items-center justify-center`}
              >
                <i
                  className={`${item.icon} ${isActiveLink(item.link)
                    ? "text-black"
                    : "text-gray-500 stroke-current"
                    }`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              className="bg-white text-sm text-primary border-none"
              side="right"
            >
              <p>{item.name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PopoverTrigger>
      <PopoverContent
        className="bg-white text-primary border-none p-1 w-fit"
        side="right"
      >
        <div className="flex flex-col gap-1">
          {item.children.map((child: ResourceTypes, index: number) => (
            <button
              key={index}
              onClick={(e) => {
                if (child.code == "daily_check_ins") {
                  e.preventDefault();
                  window.open(item.link, '_blank');
                  return;
                }
                navigate(child.link)
              }}
              className={`text-left text-sm rounded-md px-3 py-1 ${isActiveLink(child.link)
                ? "bg-primary text-white"
                : "hover:bg-muted text-gray-500"
                }`}
            >
              {child.name}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};